/**
 * Maintenance Page - View Aquarimate maintenance activities
 */
import { useState, useMemo } from 'react';
import {
  Container,
  Title,
  Stack,
  Paper,
  Alert,
  Loader,
  Center,
} from '@mantine/core';
import { DatePickerInput, type DatesRangeValue } from '@mantine/dates';
import { IconAlertCircle } from '@tabler/icons-react';
import { useMaintenanceActivities } from '@/hooks/useMaintenanceActivities';
import { MaintenanceSummary } from '@/features/maintenance/MaintenanceSummary';
import { MaintenanceFilters } from '@/features/maintenance/MaintenanceFilters';
import { MaintenanceCharts } from '@/features/maintenance/MaintenanceCharts';
import { MaintenanceTable } from '@/features/maintenance/MaintenanceTable';
import type { MaintenanceActivityFilters } from '@/api/maintenance';
import type { MaintenanceActivity } from '@/types/maintenance';

export default function MaintenancePage() {
  // Default to last 180 days
  const defaultEndDate = new Date();
  const defaultStartDate = new Date();
  defaultStartDate.setDate(defaultStartDate.getDate() - 180);

  const [dateRange, setDateRange] = useState<DatesRangeValue>([
    defaultStartDate,
    defaultEndDate,
  ]);

  // Local filter state (for client-side filtering)
  const [selectedTaskTypes, setSelectedTaskTypes] = useState<string[]>([]);
  const [selectedTaskTitles, setSelectedTaskTitles] = useState<string[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<string[]>([]);
  const [performedOnly, setPerformedOnly] = useState(true);

  // Timeline view state
  const [timelineView, setTimelineView] = useState<'activities' | 'equipment'>('activities');

  // Auto-compute filters from date range (like DosetronicPage)
  const filters: MaintenanceActivityFilters = {
    performed_only: performedOnly,
  };
  if (dateRange[0] && dateRange[0] instanceof Date) {
    filters.start_date = dateRange[0].toISOString().split('T')[0];
  }
  if (dateRange[1] && dateRange[1] instanceof Date) {
    filters.end_date = dateRange[1].toISOString().split('T')[0];
  }

  // Fetch maintenance activities with React Query (filters auto-update when dateRange changes)
  const { data: response, isLoading, error } = useMaintenanceActivities(filters);

  // Apply client-side filters
  const filteredActivities = useMemo(() => {
    if (!response?.data) return [];

    let filtered: MaintenanceActivity[] = response.data;

    // Filter by task types (if selected)
    if (selectedTaskTypes.length > 0) {
      const taskTypeNumbers = selectedTaskTypes.map((t) => Number(t));
      filtered = filtered.filter((a) => taskTypeNumbers.includes(a.task_type));
    }

    // Filter by task titles (if selected)
    if (selectedTaskTitles.length > 0) {
      filtered = filtered.filter((a) => selectedTaskTitles.includes(a.task_title));
    }

    // Filter by equipment (if selected)
    if (selectedEquipment.length > 0) {
      filtered = filtered.filter(
        (a) => a.equipment_name && selectedEquipment.includes(a.equipment_name)
      );
    }

    return filtered;
  }, [response?.data, selectedTaskTypes, selectedTaskTitles, selectedEquipment]);

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Title order={1}>🔧 Maintenance Activities</Title>

        {/* Error State */}
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error loading maintenance activities"
            color="red"
          >
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </Alert>
        )}

        {/* Date Range Controls */}
        <Paper p="md" withBorder>
          <DatePickerInput
            type="range"
            label="Date Range"
            placeholder="Pick dates range"
            defaultValue={dateRange}
            onChange={(dates: DatesRangeValue) => {
              // Handle partial selection (user clicked first date but not second)
              if (!dates) return;

              if (Array.isArray(dates)) {
                const [start, end] = dates;

                // Only update state if both dates are selected
                if (start && end) {
                  const startDate = start instanceof Date ? start : new Date(start);
                  const endDate = end instanceof Date ? end : new Date(end);
                  setDateRange([startDate, endDate]);
                }
              }
            }}
            maxDate={new Date()}
            clearable={false}
            valueFormat="MMM DD, YYYY"
            allowSingleDateInRange
            style={{ width: 300 }}
          />
        </Paper>

        {/* Loading State */}
        {isLoading && (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        )}

        {/* Data Sections */}
        {!isLoading && response && (
          <>
            {/* Summary Statistics */}
            <MaintenanceSummary activities={filteredActivities} />

            {/* Filter Controls */}
            <MaintenanceFilters
              activities={response.data}
              selectedTaskTypes={selectedTaskTypes}
              onTaskTypesChange={setSelectedTaskTypes}
              selectedTaskTitles={selectedTaskTitles}
              onTaskTitlesChange={setSelectedTaskTitles}
              selectedEquipment={selectedEquipment}
              onEquipmentChange={setSelectedEquipment}
              performedOnly={performedOnly}
              onPerformedOnlyChange={setPerformedOnly}
            />

            {/* Activity Charts */}
            <MaintenanceCharts
              activities={filteredActivities}
              timelineView={timelineView}
              onTimelineViewChange={setTimelineView}
            />

            {/* Activity Details Table */}
            <MaintenanceTable activities={filteredActivities} />
          </>
        )}
      </Stack>
    </Container>
  );
}
